import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell, BadgeAlt as Badge, } from '@cargomatic/ui';
import { titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';
import { getShipmentDetailUrl, getEquipment } from 'utils/formatters/shipments';
import { sluggify } from 'utils/localization/utils';
import PODButton from 'components/modules/accounting/PODButton';
import ExternalLinkItem from 'components/v2/ExternalLinkItem';
function buildFlatRows(invoice) {
    var _a, _b, _c, _d, _e;
    const rows = [];
    for (const group of (_a = invoice.lineItemGroups) !== null && _a !== void 0 ? _a : []) {
        const orderRef = (_d = (_c = (_b = group.referenceNumbers) === null || _b === void 0 ? void 0 : _b.find((r) => r.name === 'order_id')) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : '';
        for (const lineItem of (_e = group.lineItems) !== null && _e !== void 0 ? _e : []) {
            rows.push({
                lineItem,
                group,
                orderRef,
                isOrderLevel: !group.shipment_id,
            });
        }
    }
    return rows;
}
function formatLocation(val) {
    if (!val)
        return '';
    if (typeof val === 'string')
        return val;
    const loc = val;
    return (loc.name ||
        loc.address ||
        (loc.city && loc.state ? `${loc.city}, ${loc.state}` : '') ||
        '');
}
export default function InvoiceV2LineItems({ invoice, orderId, }) {
    const { t } = useTranslation();
    const rows = buildFlatRows(invoice);
    if (rows.length === 0) {
        return (React.createElement("div", { className: "text-center text-muted-foreground py-8" }, t('NO_DATA_FOUND')));
    }
    return (React.createElement("div", { className: "overflow-x-auto" },
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null,
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('CHARGE'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('AMOUNT'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('ORDER'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('SHIPMENT'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('EQUIPMENT'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, t('DELIVER_BY')),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, t('CLOSED_DATE')),
                    React.createElement(TableHead, { className: "whitespace-nowrap min-w-[120px]" }, titleize(t('ORIGIN'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap min-w-[120px]" }, titleize(t('DESTINATION'))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('DRIVER', { count: 1 }))),
                    React.createElement(TableHead, { className: "whitespace-nowrap" }, "POD"))),
            React.createElement(TableBody, null, rows.map(({ lineItem, group, orderRef, isOrderLevel }) => {
                var _a, _b, _c;
                const chargeMessage = lineItem.type === 'customer_adjustment'
                    ? titleize((_a = lineItem.message) !== null && _a !== void 0 ? _a : '')
                    : t(sluggify((_b = lineItem.message) !== null && _b !== void 0 ? _b : ''));
                return (React.createElement(TableRow, { key: lineItem._id },
                    React.createElement(TableCell, null,
                        React.createElement("div", { className: "flex items-center gap-2 whitespace-nowrap" },
                            chargeMessage,
                            isOrderLevel && (React.createElement(Badge, { className: "bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200" }, "Order-Level")))),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, formatMoney(lineItem.value, lineItem.currency)),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, orderId && orderRef ? (React.createElement(ExternalLinkItem, { to: `/orders/${orderId}`, label: orderRef })) : (orderRef || '-')),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, group.shipment_id ? (React.createElement(ExternalLinkItem, { to: getShipmentDetailUrl(group.shipment_id), label: group.shipmentId || group.shipment_id })) : ('-')),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_c = group.equipments) === null || _c === void 0 ? void 0 : _c.length)
                        ? getEquipment(group.equipments)
                        : '-'),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, group.jobDeliverBy
                        ? moment(group.jobDeliverBy).format('L')
                        : '-'),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, group.jobClosed ? moment(group.jobClosed).format('L') : '-'),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, formatLocation(group.origin)),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, formatLocation(group.destination)),
                    React.createElement(TableCell, { className: "whitespace-nowrap" }, group.driverName || '-'),
                    React.createElement(TableCell, { className: "whitespace-nowrap" },
                        React.createElement(PODButton, { pod: group.pod, podUrl: group.podUrl }))));
            })))));
}
